import { CheckOutlined, ClockCircleOutlined, MailOutlined, PhoneOutlined, RedoOutlined } from '@ant-design/icons';
import { App, Button, Descriptions, Drawer, Empty, Input, Segmented, Space, Tag } from 'antd';
import { useState } from 'react';
import { api } from '../api';
import { Bone, ErrorNotice, PageHeader, Panel } from '../components';
import { formatDate, useLoad } from '../lib';

const typeLabels = { demo: 'Demo', sales: 'Sales', support: 'Support', partner: 'Partner', other: 'Other' };

export function EnquiriesPage() {
  const { message } = App.useApp();
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api('/v1/admin/enquiries').then((body) => body.enquiries));
  const rows = (data ?? []).filter((row) => filter === 'all' || row.status === filter);
  const pending = (data ?? []).filter((row) => row.status === 'pending').length;

  function open(row) {
    setSelected(row);
    setNote(row.note || '');
  }

  async function mark(status) {
    setSaving(true);
    try {
      const body = await api(`/v1/admin/enquiries/${selected.id}`, { method: 'PUT', body: JSON.stringify({ status, note }) });
      message.success(status === 'complete' ? 'Enquiry marked complete.' : 'Enquiry moved back to pending.');
      setSelected(body.enquiry);
      await reload();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Could not update the enquiry');
    } finally {
      setSaving(false);
    }
  }

  return <>
    <PageHeader title="Contact enquiries" description="Messages submitted through the public website. Review each enquiry and mark it complete when your team has handled it." />
    <ErrorNotice error={error} />
    <Panel flush title="Inbox" description={loading && !data ? <Bone width={100} height={11} /> : `${pending} pending · ${(data ?? []).length} total`} extra={
      <Segmented value={filter} onChange={setFilter} options={[{ label: `Pending (${pending})`, value: 'pending' }, { label: 'Complete', value: 'complete' }, { label: 'All', value: 'all' }]} />
    }>
      {loading && !data ? <div className="enquiry-list" aria-busy>{Array.from({ length: 4 }, (_, index) => <div className="enquiry-row" key={index}><Bone width={42} height={42} round /><div className="bone-lines"><Bone width={180} height={13} /><Bone width="72%" height={10} /></div></div>)}</div> : rows.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={filter === 'pending' ? 'No pending enquiries' : 'No enquiries in this view'} style={{ padding: 40 }} /> : <div className="enquiry-list">
        {rows.map((row) => <button type="button" className="enquiry-row" key={row.id} onClick={() => open(row)}>
          <span className={`feed-icon ${row.status === 'complete' ? 'tone-green' : 'tone-amber'}`}>{row.status === 'complete' ? <CheckOutlined /> : <ClockCircleOutlined />}</span>
          <span className="enquiry-main">
            <span className="enquiry-title"><strong>{row.name}</strong><Tag variant="filled">{typeLabels[row.type] || row.type}</Tag>{row.status === 'complete' && <Tag color="success" variant="filled">Complete</Tag>}</span>
            <span className="enquiry-preview">{row.company || row.city || row.message || 'No additional details'}</span>
            <span className="cell-sub">{row.mobile}{row.email ? ` · ${row.email}` : ''} · {formatDate(row.createdAt)}</span>
          </span>
        </button>)}
      </div>}
    </Panel>

    <Drawer title={selected?.name || 'Enquiry'} open={Boolean(selected)} onClose={() => setSelected(null)} size={560} footer={selected && <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {selected.status === 'complete' ? <Button icon={<RedoOutlined />} loading={saving} onClick={() => void mark('pending')}>Move to pending</Button> : <Button type="primary" icon={<CheckOutlined />} loading={saving} onClick={() => void mark('complete')}>Mark complete</Button>}
    </Space>}>
      {selected && <div className="enquiry-detail">
        <Tag color={selected.status === 'complete' ? 'success' : 'warning'} variant="filled">{selected.status === 'complete' ? 'Complete' : 'Pending'}</Tag>
        <Descriptions column={1} colon={false} size="middle" styles={{ label: { width: 120, color: 'var(--muted)' } }}>
          <Descriptions.Item label="Type">{typeLabels[selected.type] || selected.type}</Descriptions.Item>
          <Descriptions.Item label="Mobile"><a href={`tel:${selected.mobile.replace(/\s/g, '')}`}><PhoneOutlined /> {selected.mobile}</a></Descriptions.Item>
          <Descriptions.Item label="Email">{selected.email ? <a href={`mailto:${selected.email}`}><MailOutlined /> {selected.email}</a> : '—'}</Descriptions.Item>
          <Descriptions.Item label="Company">{selected.company || '—'}</Descriptions.Item>
          <Descriptions.Item label="City">{selected.city || '—'}</Descriptions.Item>
          <Descriptions.Item label="Received">{formatDate(selected.createdAt)}</Descriptions.Item>
        </Descriptions>
        <div><h4>Message</h4><p className="enquiry-message">{selected.message || 'No message provided.'}</p></div>
        <div><h4>Internal note</h4><Input.TextArea value={note} onChange={(event) => setNote(event.target.value)} rows={4} maxLength={2000} placeholder="Call outcome, owner or follow-up details…" /></div>
      </div>}
    </Drawer>
  </>;
}
